import cn from 'classnames'
import { Table } from 'legacy/lut/library/list'
import { FC, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'

import { isBusinessPriorityActiveInMarket } from '../../lib/isBusinessPriorityActiveInMarket'
import { useBusinessPrioritiesView } from '../../model'
import {
  selectFilteredBusinessPriorities,
  selectIsBusinessPrioritiesFetching,
} from '../../model/selectors'
import { requestBusinessPrioritiesChangeStatus } from '../../model/thunks/requestBusinessPrioritiesChangeStatus'
import { requestBusinessPrioritiesDelete } from '../../model/thunks/requestBusinessPrioritiesDelete'
import { requestBusinessPrioritiesSetAllMarketsStatus } from '../../model/thunks/requestBusinessPrioritiesSetAllMarketsStatus'
import { IBusinessPriority } from '../../model/types'

import { ActivationConfirmationDialog } from './activationConfirmation/ActivationConfirmationDialog'
import { DeleteConfirmationDialog } from './deleteConfirmation/DeleteConfirmationDialog'
import { getColumns } from './helpers/getColumns'
import { getRowActions } from './helpers/getRowActions'
import { SwitchConfirmationDialog } from './switchConfirmation/SwitchConfirmationDialog'
import { useConfirmation } from './useConfirmation'

import styles from './BusinessPrioritiesTable.module.scss'

interface IBusinessPrioritiesTableProps {
  isWorldwide: boolean
  market?: string
  isReadOnly?: boolean
  className?: string
}

export const BusinessPrioritiesTable: FC<IBusinessPrioritiesTableProps> = ({
  isWorldwide,
  market = '',
  isReadOnly = false,
  className,
}) => {
  const records = useSelector(selectFilteredBusinessPriorities)
  const isLoading = useSelector(selectIsBusinessPrioritiesFetching)
  const dispatch = useAppDispatch()
  const { goToEditView } = useBusinessPrioritiesView()
  const switchConfirmation = useConfirmation<'on' | 'off'>('off')
  const activationConfirmation = useConfirmation<'activate' | 'deactivate'>('deactivate')
  const deleteConfirmation = useConfirmation()

  const handleDelete = useCallback(
    async (record: IBusinessPriority) => {
      if (await deleteConfirmation.confirm()) {
        dispatch(requestBusinessPrioritiesDelete(record))
      }
    },
    [deleteConfirmation, dispatch]
  )

  const handleBusinessPrioritiySwitch = useCallback(
    async (record: IBusinessPriority) => {
      const actionType = isBusinessPriorityActiveInMarket(record, market) ? 'off' : 'on'

      if (await switchConfirmation.confirm(actionType)) {
        dispatch(requestBusinessPrioritiesChangeStatus(record))
      }
    },
    [switchConfirmation, dispatch, market]
  )

  const handleDeactivate = useCallback(
    async (record: IBusinessPriority) => {
      if (await activationConfirmation.confirm('deactivate')) {
        dispatch(requestBusinessPrioritiesSetAllMarketsStatus({ record, status: 'off' }))
      }
    },
    [activationConfirmation, dispatch]
  )

  const handleActivate = useCallback(
    async (record: IBusinessPriority) => {
      if (await activationConfirmation.confirm('activate')) {
        dispatch(requestBusinessPrioritiesSetAllMarketsStatus({ record, status: 'on' }))
      }
    },
    [activationConfirmation, dispatch]
  )

  const columns = useMemo(() => getColumns({ isWorldwide, market }), [isWorldwide, market])

  const rowActions = useMemo(
    () =>
      getRowActions({
        market,
        onEdit: goToEditView,
        onDelete: handleDelete,
        onSwitchOn: handleBusinessPrioritiySwitch,
        onSwitchOff: handleBusinessPrioritiySwitch,
        onActivate: handleActivate,
        onDeactivate: handleDeactivate,
      }),
    [
      market,
      goToEditView,
      handleDelete,
      handleBusinessPrioritiySwitch,
      handleActivate,
      handleDeactivate,
    ]
  )

  return (
    <div className={cn(styles.root, className)}>
      <SwitchConfirmationDialog
        {...switchConfirmation.dialogProps}
        actionType={switchConfirmation.data}
      />
      <ActivationConfirmationDialog
        {...activationConfirmation.dialogProps}
        actionType={activationConfirmation.data}
      />
      <DeleteConfirmationDialog {...deleteConfirmation.dialogProps} />
      <LoaderOverlay isVisible={isLoading} />
      <Table<IBusinessPriority>
        items={records}
        keyField="uid"
        header
        classes={{
          header: styles.headerRow,
        }}
        columns={columns}
        rowActions={isReadOnly ? undefined : rowActions}
        rowActionsVisible="visible"
      />
    </div>
  )
}

BusinessPrioritiesTable.displayName = 'BusinessPrioritiesTable'

export default BusinessPrioritiesTable
